import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import Autocomplete from '../../../../components/autocomplete';
import { Options, Select } from '../../../../components/form/select';
import { Input } from '../../../../components/input';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import {
	ClabeError,
	CurrencyData,
	InternationalProp,
	Steps,
	TransferResumeItem,
} from '../../../../models/international';
import { InternationalServices } from '../../../../services/international';
import { onlyNumberRegexWithDot } from '../../../../utils/validations';
import InternationalResume from '../internationalResume';
import {
	Button,
	ButtonContainer,
	DivSelect,
	FlexDiv,
	HintInputError,
	InputContainer,
	ModalContainer,
	ModalStep,
	ModalTitle,
} from './styles';

const ModalInternational = ({ onClose }: InternationalProp) => {
	const { handleShowAlert } = useFloatingAlert();
	const { merchantSelected } = useMerchant();
	const [steps, setSteps] = useState<Steps>({ step: 1, errorClabe: false });
	const [errorClabe, setErrorClabe] = useState<ClabeError>({
		value: false,
		label: '',
	});
	const [stateModal, setStateModal] = useState({
		trackingkey: '',
		concept: 'Internacional',
		account: '',
		clientName: '',
		country: '',
		amount: '',
		conversionAmount: '',
		badge: 'AUD',
		badgeReturn: 'AUD',
		loading: true,
	});
	const [conversion, setConversion] = useState<string>('0.00');

	const [updatedData, setUpdatedData] = useState<CurrencyData>({
		currencies: [{ value: 0, label: '', customLabel: '', show: true }],
	});

	const { data: dataCurrentCount } = useQuery({
		queryFn: () => InternationalServices.getCurrencies(),
		onSuccess: (data) => {
			if (data) {
				const updatedCurrencies = data.currencies.map((current, index) => ({
					value: index,
					label: current,
					customLabel: current,
					show: true,
				}));
				setUpdatedData({ ...data, currencies: updatedCurrencies });
				setStateModal({ ...stateModal, loading: false });
			} else {
				setUpdatedData(data);
				setStateModal({ ...stateModal, loading: false });
			}
		},
	});

	useEffect(() => {
		const amout =
			parseFloat(stateModal.amount || '0.0') * parseFloat(stateModal.conversionAmount || '0.0');
		setConversion(amout.toFixed(5).toString());
	}, [stateModal.amount, stateModal.conversionAmount]);

	const handleKeyDownValidateAmount = (e: KeyboardEvent<HTMLInputElement>) => {
		if (!onlyNumberRegexWithDot.test(e.key) && e.key !== 'Backspace') {
			e.preventDefault();
		}
	};

	const handleVerifyDisabledButton = {
		firstStep: () => {
			if (
				stateModal.trackingkey !== '' &&
				stateModal.account.length > 3 &&
				stateModal.clientName !== '' &&
				stateModal.country &&
				stateModal.amount !== '' &&
				stateModal.conversionAmount !== '' &&
				!errorClabe.value
			) {
				return false;
			} else {
				return true;
			}
		},
		thirdStep: () => {
			if (stateModal.badge !== '') return false;
			return true;
		},
	};

	const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;

		const regex = /^[0-9]*[.]?([0-9]{1,5})?$/;
		const conceptRegex = /^[a-zA-Z0-9 ]{0,40}$/;
		const nameRegex = /^[a-zA-Z ]{0,40}$/;
		if (name === 'clientName' && !nameRegex.test(value)) {
			return;
		}
		if (name === 'trackingKey' && !conceptRegex.test(value)) {
			return;
		}
		if (name === 'amount' && !regex.test(value)) {
			return;
		}
		if (name === 'conversionAmount' && !regex.test(value)) {
			return;
		}
		setStateModal({ ...stateModal, [name]: value });
	};

	const handleChangeSelectBadge = (option: Options) => {
		const { label } = option;
		typeof label == 'string' && setStateModal({ ...stateModal, badge: label });
	};

	const handleChangeSelectBadgeReturn = (option: Options) => {
		const { label } = option;
		typeof label == 'string' && setStateModal({ ...stateModal, badgeReturn: label });
	};

	const handleContinue = async () => {
		await setStateModal({ ...stateModal, loading: true });
		try {
			const data = await InternationalServices.postGenerateInternational({
				id: stateModal.trackingkey,
				clabe: stateModal.account,
				beneficiary: stateModal.clientName,
				amount: parseFloat(stateModal.amount),
				concept: stateModal.concept,
				currency: stateModal.badge,
				country: stateModal.country,
				exchange: parseFloat(stateModal.conversionAmount),
				merchantCode: merchantSelected?.code as string,
			});
			if (data) {
				handleShowAlert({
					type: 'success',
					message: 'Transferencia internacional guardada',
				});
				onClose();
				return data;
			}
		} catch (error) {
			await setStateModal({ ...stateModal, loading: false });
			handleShowAlert({
				type: 'error',
				message: 'La clave de rastreo ya fue utilizada',
			});
			return null;
		}
	};

	const handleChangeClabe = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const conceptRegex = /^[0-9]{0,40}$/;
		if (name == 'account') {
			if (!conceptRegex.test(value)) {
				return;
			}
			if (value.length < 3) {
				setErrorClabe({
					value: true,
					label: 'Debe tener más de 3 dígitos',
				});
			} else {
				setErrorClabe({
					value: false,
					label: 'Debe tener más de 3 dígitos',
				});
			}
			setStateModal({ ...stateModal, [name]: value });
		}
	};

	const formResume: TransferResumeItem[] = [
		{
			item: 'Monto en divisa de destino',
			data: `${conversion} ${stateModal.badgeReturn}`,
		},
		{
			item: 'Tipo de cambio',
			data: `${stateModal.conversionAmount} ${stateModal.badgeReturn}`,
		},
		{
			item: 'Número de cuenta',
			data: stateModal.account,
		},
		{
			item: 'Nombre beneficiario',
			data: stateModal.clientName,
		},
		{
			item: 'País de destino',
			data: stateModal.country,
		},
		{
			item: 'ID',
			data: stateModal.trackingkey,
		},
	];

	return (
		<Modal
			title='Remesas'
			show
			type="medium"
			onClose={onClose}
		>
			{stateModal.loading ? (
				<Spinner />
			) : (
				<ModalContainer>
					<ModalStep>PASO {steps.step} DE 2</ModalStep>

					<ModalTitle>
						{steps.step === 1 && 'Completa la información del pago'}
						{steps.step === 2 && 'Verifica la información del pago'}
					</ModalTitle>

					{steps.step === 1 && (
						<>
							<InputContainer>
								<FlexDiv>
									<Input
										label="Monto"
										name="amount"
										variant="large"
										value={stateModal.amount}
										onChange={handleChangeInput}
										prefixSymbol="$"
										onKeyDown={handleKeyDownValidateAmount}
									/>
									<DivSelect>
										<Select
											titleLabel="Divisa"
											size="large"
											defaultOption={
												updatedData.currencies.find(
													(currency) => currency.label === stateModal.badge,
												)?.value || 0
											}
											options={updatedData.currencies}
											onChange={handleChangeSelectBadge}
											sizeHeight={'min'}
										/>
									</DivSelect>
								</FlexDiv>
								<FlexDiv>
									<Input
										label="Tipo de cambio"
										name="conversionAmount"
										variant="large"
										value={stateModal.conversionAmount}
										onChange={handleChangeInput}
										prefixSymbol="$"
										onKeyDown={handleKeyDownValidateAmount}
									/>
									<DivSelect>
										<Select
											titleLabel="Divisa de destino"
											size="large"
											defaultOption={
												updatedData.currencies.find(
													(currency) => currency.label === stateModal.badgeReturn,
												)?.value || 0
											}
											options={updatedData.currencies}
											onChange={handleChangeSelectBadgeReturn}
											sizeHeight={'min'}
										/>
									</DivSelect>
								</FlexDiv>
								<div>
									<Input
										label="Monto en divisa de destino"
										name="amount"
										variant="large"
										disabled
										value={conversion || '0.00'}
										placeholder="0.00"
										prefixSymbol="$"
									/>
								</div>
								<div>
									<Input
										label="Número de cuenta"
										name="account"
										variant="large"
										value={stateModal.account}
										onChange={handleChangeClabe}
									/>
									{errorClabe.value && <HintInputError>{errorClabe.label}</HintInputError>}
								</div>

								<div>
									<Input
										label="Nombre del beneficiario (máx. 40 caracteres)"
										name="clientName"
										variant="large"
										value={stateModal.clientName}
										onChange={handleChangeInput}
										maxLength={40}
									/>
								</div>
								<div>
									<Autocomplete
										suggestions={dataCurrentCount?.countries}
										pick={(value) => setStateModal({ ...stateModal, country: value })}
										value={stateModal.country}
										placeholder={'País de destino'}
									></Autocomplete>
								</div>
								<div>
									<Input
										label="ID"
										name="trackingkey"
										variant="large"
										value={stateModal.trackingkey}
										onChange={handleChangeInput}
									/>
								</div>
							</InputContainer>

							<Button
								onClick={() => {
									setSteps({ ...steps, step: 2 });
								}}
								disabled={handleVerifyDisabledButton.firstStep()}
								outboundColor
							>
								Siguiente
							</Button>
						</>
					)}

					{steps.step === 2 && <InternationalResume transferResume={formResume} />}

					{steps.step === 2 && (
						<ButtonContainer>
							<Button
								onClick={() => {
									setSteps({ ...steps, step: 1 });
								}}
								disabled={handleVerifyDisabledButton.firstStep()}
							>
								Regresar
							</Button>
							<Button
								onClick={() => {
									handleContinue();
								}}
								disabled={handleVerifyDisabledButton.firstStep()}
								outboundColor
							>
								Confirmar
							</Button>
						</ButtonContainer>
					)}
				</ModalContainer>
			)}
		</Modal>
	);
};

export default ModalInternational;
